import { IconClockPause, IconLock } from "@tabler/icons-react";

import type { RegisteredKey } from "../api/types";
import { SshKeySection } from "../components/account/SshKeySection";
import { GateCard } from "./GateCard";

/**
 * "Ask X" — or nothing at all when the deployment named nobody.
 *
 * The contact is served by `GET /api/v1/auth/config` (`SUPPORT_CONTACT`, or
 * the first `ADMIN_EMAILS` entry), because it used to be a constant compiled
 * into this bundle and pointed at an alert firehose nobody reads.
 *
 * Args:
 *     contact: Who to ask, when the server names somebody.
 *
 * Returns:
 *     The sentence to render.
 */
function AskFor({ contact }: { contact: string | null }): JSX.Element {
  if (!contact) {
    return (
      <>
        Ask whoever runs the GPU console — the person who sent you this link.
      </>
    );
  }
  return (
    <>
      Ask <span className="mono">{contact}</span>.
    </>
  );
}

interface PendingScreenProps {
  /** Who the server says you are; null when it would not even say that. */
  email: string | null;
  /** Who grants research access, as the server publishes it. */
  accessContact: string | null;
  /** The keys already on file, as the server lists them. */
  sshPublicKeys: RegisteredKey[];
  /** Extra sentence the server sent with its refusal. */
  serverMessage?: string | null;
  /** Ask the server again whether the account has been approved. */
  onCheckAgain: () => void;
  onSignOut: () => void;
}

/**
 * Signed in, and allowed to do exactly nothing yet.
 *
 * A first Google sign-in creates a `pending` account (`docs/AUTH.md` §2). This
 * is the whole console for that person until somebody approves them, so it has
 * to be complete rather than apologetic: who they are signed in as, what is
 * missing, who grants it, and the one thing they *can* do now — register the
 * SSH key their first workspace will need.
 *
 * Args:
 *     email: The signed-in address.
 *     accessContact: Who grants research access, as the server names them.
 *     sshPublicKeys: The keys already on file, if any.
 *     serverMessage: What the server said when it refused.
 *     onCheckAgain: Re-read the account.
 *     onSignOut: End the session.
 *
 * Returns:
 *     The waiting-for-approval screen.
 */
export function PendingScreen({
  email,
  accessContact,
  sshPublicKeys,
  serverMessage = null,
  onCheckAgain,
  onSignOut,
}: PendingScreenProps): JSX.Element {
  return (
    <GateCard title="Your account is waiting for approval" wide>
      <p className="gate__badge">
        <IconClockPause size={14} aria-hidden="true" />
        Waiting for approval
      </p>

      <p className="gate__text">
        You are signed in{email ? " as " : ""}
        {email ? <span className="mono">{email}</span> : null}. Signing in is
        not access: somebody has to grant you research access before you can
        take GPUs, and that is a person's decision, not a delay.
      </p>

      <p className="gate__text">
        <AskFor contact={accessContact} /> Approval takes a few seconds once
        somebody reads it — you do not have to sign in again afterwards, this
        page picks it up.
      </p>

      {serverMessage ? <p className="gate__text">{serverMessage}</p> : null}

      <div className="gate__actions">
        <button type="button" className="btn" onClick={onCheckAgain}>
          Check again
        </button>
        <button type="button" className="btn btn--ghost" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      <div className="gate__block">
        <p className="gate__text">
          While you wait, add the SSH keys your workspaces will use — one for
          each machine you connect from. They are saved straight away, so the
          moment you are approved you can go from nothing to a shell without a
          detour.
        </p>
        <SshKeySection
          storedKeys={sshPublicKeys}
          formTitle="Add your SSH key now"
          note="These keys are ready for your first workspace."
        />
      </div>
    </GateCard>
  );
}

interface DisabledScreenProps {
  email: string | null;
  /** Who to ask about it, as the server publishes it. */
  accessContact: string | null;
  /** Why access was turned off, as the server worded it. */
  reason: string | null;
  onSignOut: () => void;
}

/**
 * Signed in, and switched off.
 *
 * Disabling is not a deletion (`docs/AUTH.md` §2): the files and the history
 * stay, and the account can be approved again — so the screen says that,
 * instead of leaving somebody to assume their work is gone.
 *
 * Args:
 *     email: The signed-in address.
 *     accessContact: Who to ask about it, as the server names them.
 *     reason: Why access was turned off.
 *     onSignOut: End the session.
 *
 * Returns:
 *     The access-turned-off screen.
 */
export function DisabledScreen({
  email,
  accessContact,
  reason,
  onSignOut,
}: DisabledScreenProps): JSX.Element {
  return (
    <GateCard title="Your access has been turned off">
      <p className="gate__badge gate__badge--danger">
        <IconLock size={14} aria-hidden="true" />
        Access turned off
      </p>

      <p className="gate__text">
        You are signed in{email ? " as " : ""}
        {email ? <span className="mono">{email}</span> : null}, but this account
        cannot start or reach workspaces.
      </p>

      {reason ? (
        <p className="gate__text">
          The reason recorded: <span className="gate__quote">{reason}</span>
        </p>
      ) : null}

      <p className="gate__text">
        Nothing has been deleted — your home folder and your history are kept,
        and the account can be turned back on. <AskFor contact={accessContact} />
      </p>

      <button type="button" className="btn" onClick={onSignOut}>
        Sign out
      </button>
    </GateCard>
  );
}
